/* Hallmark · genre: editorial · macrostructure: Marquee Hero · theme: project tokens (orange --primary)
 * H1 Marquee knobs: size=xl, alignment=left-bias, underlay=single rule below
 * polish: HP4 decorative numeral — the discount sits behind the statement rather than
 * becoming the headline, so this page does not repeat the deposit page's Stat-Led fold.
 * states: default · hover · focus · active · disabled · loading · error · success
 */
import { Button } from '@/components/ui/button';
import { useVoucherCopy } from '@/hooks/use-voucher-copy';
import { getImageUrl } from '@/lib/image';
import { cn } from '@/lib/utils';
import { type VoucherDetail, type VoucherScope, type VoucherScopeTarget, type VoucherState } from '@/types/voucher';
import { AlertTriangle, Check, Copy, Loader2, Store, Ticket } from 'lucide-react';

interface VoucherHeroProps {
  voucher: VoucherDetail;
  state: VoucherState;
  redeemable: boolean;
  scope: VoucherScope;
  target: VoucherScopeTarget | null;
}

/** What the voucher covers, said in one line above the statement. */
function coverageLine(scope: VoucherScope, target: VoucherScopeTarget | null): string {
  if (!target) return 'Áp dụng cho toàn bộ hệ thống';

  return {
    site: `Áp dụng tại ${target.name}`,
    rate: `Áp dụng cho gói ${target.name}`,
    service: `Áp dụng cho dịch vụ ${target.name}`,
    global: 'Áp dụng cho toàn bộ hệ thống',
  }[scope];
}

const STATE_NOTICE: Record<Exclude<VoucherState, 'valid'>, string> = {
  expired: 'Ưu đãi đã kết thúc',
  used_up: 'Ưu đãi đã hết lượt sử dụng',
};

function formatExpiry(expiresAt?: string | null): string | null {
  if (!expiresAt) return null;
  const date = new Date(expiresAt);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function VoucherHero({ voucher, state, redeemable, scope, target }: VoucherHeroProps) {
  const { copyState, copy } = useVoucherCopy();
  const artwork = voucher.image ? getImageUrl(voucher.image, 'voucher') : null;
  const expiry = formatExpiry(voucher.expires_at);
  const headline = voucher.title?.trim() || `Ưu đãi ${voucher.code}`;

  const copyLabel = { idle: 'Copy', copying: 'Đang copy', copied: 'Đã copy', error: 'Thử lại' }[copyState];
  const CopyStateIcon = { idle: Copy, copying: Loader2, copied: Check, error: AlertTriangle }[copyState];

  const meta = [
    voucher.remaining_quantity !== null && voucher.remaining_quantity !== undefined ? `Còn ${voucher.remaining_quantity} mã` : null,
    expiry ? `HSD ${expiry}` : null,
  ].filter(Boolean);

  return (
    <section className={cn('relative isolate overflow-hidden', artwork ? 'bg-gray-900' : 'bg-white')}>
      {artwork && (
        <>
          {/* The fold background, and the page's LCP element — never lazy. */}
          <img src={artwork} alt="" fetchPriority="high" decoding="async" className="absolute inset-0 -z-10 size-full object-cover" />
          <div className="absolute inset-0 -z-10 bg-gradient-to-r from-black/80 via-black/60 to-black/25" aria-hidden="true" />
        </>
      )}

      {/* HP4. Decorative only — the discount is announced in the ticket below, so this
          is hidden from assistive tech. `overflow-hidden` on the section plus a vw-bound
          clamp keeps it inside the fold at 320px. */}
      {voucher.discount_label && (
        <span
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute -right-4 bottom-0 -z-10 leading-[0.75] font-black tracking-tighter select-none',
            artwork ? 'text-white/10' : 'text-primary/10',
          )}
          style={{ fontSize: 'clamp(6rem, 26vw, 18rem)' }}
        >
          {voucher.discount_label}
        </span>
      )}

      <div className="container mx-auto max-w-6xl px-4 pt-12 pb-16 md:pt-16 md:pb-24">
        <div className="max-w-2xl">
          <p className={cn('mb-4 text-sm font-medium', artwork ? 'text-white/80' : 'text-muted-foreground-strong')}>
            {coverageLine(scope, target)}
          </p>

          <h1
            className={cn(
              'font-bold tracking-tight text-balance',
              artwork ? 'text-white' : 'text-gray-900',
              // Long admin-written titles step down rather than overflowing.
              headline.length > 50 ? 'text-3xl md:text-4xl' : 'text-4xl md:text-5xl lg:text-6xl',
            )}
            style={{ overflowWrap: 'anywhere', minWidth: 0 }}
          >
            {headline}
          </h1>

          {/* The ticket, not a call to action — it is the object the visitor came for.
              The page's first CTA sits below the rule. */}
          <div className="voucher-shadow mt-8 max-w-md">
            <div className="voucher-ticket bg-card text-card-foreground border-primary overflow-hidden rounded-2xl border-2 border-dashed">
              <div className="flex items-center gap-3 px-5 py-4">
                <Ticket className="text-primary size-5 shrink-0" aria-hidden="true" />
                <div className="min-w-0">
                  <p className="text-primary-ink text-lg leading-tight font-bold">
                    {voucher.discount_label ? `Giảm ${voucher.discount_label}` : 'Ưu đãi'}
                  </p>
                  {meta.length > 0 && <p className="text-muted-foreground-strong mt-0.5 text-xs">{meta.join(' · ')}</p>}
                </div>
              </div>

              <div className="voucher-stub flex items-center justify-between gap-3 px-5">
                <span className="text-primary-ink min-w-0 font-mono text-base font-bold tracking-wider break-all">{voucher.code}</span>

                {state === 'valid' ? (
                  <Button
                    type="button"
                    size="sm"
                    variant={copyState === 'copied' ? 'success' : copyState === 'error' ? 'warning' : 'default'}
                    onClick={() => copy(voucher.code)}
                    disabled={copyState === 'copying'}
                    className="min-h-11 shrink-0 gap-2"
                  >
                    <CopyStateIcon className={cn('size-4', copyState === 'copying' && 'motion-safe:animate-spin')} aria-hidden="true" />
                    {copyLabel}
                  </Button>
                ) : (
                  <span className="text-muted-foreground-strong shrink-0 text-xs font-medium">{STATE_NOTICE[state]}</span>
                )}
              </div>
            </div>
          </div>

          <span aria-live="polite" className="sr-only">
            {copyState === 'copied' ? `Đã sao chép mã ${voucher.code}` : ''}
            {copyState === 'error' ? 'Không sao chép được mã, vui lòng chép thủ công' : ''}
          </span>

          {/* A fixed-amount code is a real promotion the cart cannot process. Saying so
              here beats letting it fail at checkout. */}
          {state === 'valid' && !redeemable && (
            <p
              className={cn(
                'mt-4 flex max-w-md items-start gap-2 rounded-xl border p-3 text-sm',
                artwork ? 'border-white/25 bg-black/40 text-white/90' : 'border-(--pill-border) bg-(--pill-bg) text-gray-700',
              )}
            >
              <Store className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
              <span>Mã này áp dụng trực tiếp tại quầy, chưa hỗ trợ đặt vé trực tuyến.</span>
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
